import { useCallback, useContext, useEffect, useState } from 'react'
import { useTranslation } from 'react-i18next';
import { useLocation } from 'react-router-dom';
import GenericPromises from '../../../../../api/GenericPromises';
import { AuthContext } from '../../../../../context/AuthContext';
import { GridColDef, GridRowsProp, GridTreeNodeWithRender, GridValueGetterParams } from '@mui/x-data-grid';
import { Menuitem } from '../../../../../interfaces/Security/menu';
import { CustomerItem } from '../../../../../interfaces/Sales/Catalogs/Customers/customerItems';
import { Spinner } from '../../../../../components/Commons/Spinner/Spinner';
import { PrimaryButton } from '../../../../../theme/buttons';
import DataTable from '../../../../../components/Tables/GridTableMaterialUI/DataTable';
import { Header } from '../../../../../components/Header';
import { UpdateCustomerItems } from './update';
import { AddCustomerItems } from './add';
import { usePermissions } from '../../../../../hooks/usePermissions';
import { DialogEntity } from '../../../../../components/Dialogs/DialogEntity';
import useSnackBar from '../../../../../components/Commons/SnackBar/useSnackBar';
import { useDates } from '../../../../../hooks/useDates';

export const TableCustomerItems = ({ ...props }) => {
    const [t] = useTranslation("global");
    const location = useLocation();
    const { TimeConverter } = useDates();
    const { GenericGetResource } = GenericPromises();
    const { user } = useContext(AuthContext);
    const [dataLoaded, setDataLoaded] = useState(false);
    const [unauthorized, setUnauthorized] = useState(true);
    const [openDialogUpdate, setOpenDialogUpdate] = useState(false);
    const [openDialogAdd, setOpenDialogAdd] = useState(false);
    const [customerItemsData, setCustomerItemsData] = useState<GridRowsProp>([]);
    const [customerItemPayload, setCustomerItemPayload] = useState({});
    const [resourceScreen, setResourceScreen] = useState<Menuitem>();
    const [messageSnack, setMessageSnack] = useState("");
    const {showSnackBar,SnackbarComponent}= useSnackBar();
    const [myPreferences, setMyPreferences] = useState({});
    const { GetResourceByUrl } = usePermissions();
    const [columnsCustomerItems, setColumnsCustomerItems] = useState<GridColDef<CustomerItem>[]>([
        { field: 'item_code', headerName: t("customeritems.fields.item_code"), headerClassName: 'header-grid-table', flex: 1, },
        { field: 'item_description', headerName: t("customeritems.fields.item_description"), headerClassName: 'header-grid-table', flex: 1, },
        { field: 'item_price', headerName: t("customeritems.fields.item_price"), headerClassName: 'header-grid-table', flex: 1, align: 'left', headerAlign: 'left', type: 'number' },
        { field: 'customer_reference', headerName: t("customeritems.fields.customer_reference"), headerClassName: 'header-grid-table', flex: 1, },
        { field: 'customer_reference_description', headerName: t("customeritems.fields.customer_reference_description"), headerClassName: 'header-grid-table', flex: 1, },
        {
            field: 'creation_date', headerName: t("generic.creation_date"), headerClassName: 'header-grid-table', type: "date", flex: 1,
            valueGetter(params) {
                return TimeConverter(params.value);
            },
        },
        { field: 'last_update_user', headerName: t("generic.last_update_user"), headerClassName: 'header-grid-table', flex: 1 },
        {
            field: 'last_update_date', headerName: t("generic.last_update_date"), headerClassName: 'header-grid-table', type: "date", flex: 1,
            valueGetter(params) {
                return TimeConverter(params.value);
            },
        },
    ]);

    const methodUsed = useCallback(() => {
        switch (location.state.method) {
            case "add":
                {
                    showSnackBar(t("generic.snackbar.add"),"success");
                    break;
                }
            case "delete":
                {
                    showSnackBar(t("generic.snackbar.delete"),"success");
                    break;
                }
        }

    }, [location.state, t]);

    const loadPreferences = async () => {
        let myPreferences = await localStorage.getItem("grid-customeritems-customers");
        if (myPreferences != null) {
            setMyPreferences(JSON.parse(myPreferences));
        }
    }

    const loadColumnsOrder = async () => {
        let myOrderColumns = await localStorage.getItem("grid-customeritems-customers-columns");
        if (myOrderColumns != null) {
            let myJson = JSON.parse(myOrderColumns);
            for (let index = 0; index < myJson.length; index++) {
                const element = myJson[index];
                if (element['type'] === 'date') {
                    element.headerName = t(`generic.${element.field}`);
                    element.valueGetter = function (params: GridValueGetterParams<CustomerItem, any, GridTreeNodeWithRender>) {
                        return TimeConverter(params.value);
                    };
                }
                if (element.field) {

                }
                else {
                    element.headerName = t(`customers.fields.${element.field}`);
                }
            }
            setColumnsCustomerItems(myJson);
        }
    }

    const handleAddRow = (newRow: CustomerItem) => {
        let myRow = {
            ...newRow,
            creation_date: new Date(),
            last_update_user: user?.user_email,
            last_update_date: new Date()
        }
        setCustomerItemsData((prevState) => [...customerItemsData, myRow]);
    };

    const handleUpdateRow = (updateRow: CustomerItem) => {
        if (customerItemsData.length === 0) {
            return;
        }
        setCustomerItemsData((prevRows) => {
            const rowToUpdateIndex = customerItemsData.findIndex(e => e.customer_items_id === updateRow.customer_items_id);

            return prevRows.map((row, index) =>
                index === rowToUpdateIndex ? updateRow : row,
            );
        });
    };

    const handleDeleteRow = (deleteRow: number) => {
        if (customerItemsData.length === 0) {
            return;
        }
        setCustomerItemsData((prevRows) => {
            const rowToDeleteIndex = customerItemsData.findIndex(e => e.customer_items_id === deleteRow);
            return [
                ...customerItemsData.slice(0, rowToDeleteIndex),
                ...customerItemsData.slice(rowToDeleteIndex + 1),
            ];
        });
    };


    useEffect(() => {
        if (location.state !== null && location.state.method) methodUsed();
        GenericGetResource(`/customeritems/bycustomerid/${props.customer_id}`)
            .then(
                (response) => {
                    setCustomerItemsData(response.data.data);

                    GetResourceByUrl(`/customeritems`)
                        .then((response1) => {
                            setResourceScreen((prev) => response1);
                            loadColumnsOrder();
                            loadPreferences();
                            setDataLoaded(true);
                        })
                        .catch((error) => {
                            showSnackBar(error.message,"error");
                            setMessageSnack(error.message);
                            setUnauthorized(false);
                        });
                }
            ).catch((error) => {
                showSnackBar(error.message,"error");
                setMessageSnack(error.message);
                setUnauthorized(false);
            });
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    return (
        <>
            {!unauthorized && <div className='screen-container d-flex flex-column justify-content-center align-items-center'> <img alt='ERROR' style={{ height: "20rem", width: "20rem" }} src={require("../../../../../assets/img/error.webp")} /> <h2>{messageSnack}</h2> </div>}
            {unauthorized && !dataLoaded && <Spinner isBox={false}/>}
            {dataLoaded && resourceScreen?.read &&
                <>
                    <div className='screen-container'>
                        {resourceScreen?.create === true &&
                            <div className='d-flex flex-row-reverse my-1'>
                                <PrimaryButton variant='outlined' className="my-1" onClick={() => { setOpenDialogAdd(true) }}>{t("generic.buttons.add")}</PrimaryButton>
                            </div>
                        }
                        <div className="d-flex justify-content-center">
                            <DataTable
                                columns={columnsCustomerItems}
                                setColumns={setColumnsCustomerItems}
                                data={customerItemsData}
                                entityId={"customer_items_id"}
                                entity={`CustomerItems`}
                                preferences={myPreferences}
                                namePreferences={"grid-customeritems-customers"}
                                nameOrderColumns={"grid-customeritems-customers-columns"}
                                isChildren={true}
                                setOpenDialog={setOpenDialogUpdate}
                                setDataRow={setCustomerItemPayload}
                            />
                        </div>
                    </div>
                    <DialogEntity
                        content={<UpdateCustomerItems customerItemPayload={customerItemPayload} setOpenDialog={setOpenDialogUpdate} permissions={resourceScreen} DeleteRow={handleDeleteRow} UpdateRow={handleUpdateRow} />}
                        open={openDialogUpdate}
                        setOpen={setOpenDialogUpdate}
                        title={<Header title={t("itemscustomer.relation")} size='sm' />}
                    />
                    <DialogEntity
                        content={<AddCustomerItems setOpenDialog={setOpenDialogAdd} customer_id={props.customer_id} business_name={props.business_name} AddNewRow={handleAddRow} />}
                        open={openDialogAdd}
                        setOpen={setOpenDialogAdd}
                        title={<Header title={t("itemscustomer.relation")} size='sm' />}
                    />
                    <SnackbarComponent />

                </>
            }
        </>
    )
}
